import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardTitle } from "@/components/ui/card";
import { paymentStatusPresentation } from "@/features/invoicing/status";
import { PAY_COPY } from "./copy";
import { payAmount, payConfirmations } from "./pay-format";
import type { PayPaymentView } from "./pay-types";

/**
 * Live payment status.
 *
 * Three things this panel is careful about:
 *
 *   - **An unknown status is "Unknown".** The label comes from the shared
 *     `paymentStatusPresentation`, which renders any status this build has not
 *     heard of as `Unknown` rather than echoing a raw wire value
 *     (`status.ts` `UNKNOWN_STATUS`). The payer is never shown `partially_paid_v2`.
 *   - **Screening is described, never explained.** While the payment is
 *     `under_review` the panel says "Payment received — under review." and
 *     nothing else. Screening outcomes, thresholds and the reasons behind a
 *     hold are compliance information and do not belong on an anonymous page.
 *   - **Polling that stopped says so.** Once the bounded retry budget is spent
 *     the panel shows the last known status, labels it, and offers a manual
 *     check — rather than presenting a frozen status as if it were live.
 */
export function StatusPanel({
  payment,
  polling,
  stale,
  onRefresh,
}: {
  payment: PayPaymentView;
  polling: boolean;
  stale: boolean;
  onRefresh: () => void;
}) {
  const presentation = paymentStatusPresentation(payment.status);
  const confirmations = payConfirmations(payment.confirmations, payment.requiredConfirmations);
  const note = statusNote(payment.status);

  return (
    <Card>
      <CardBody>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle>{PAY_COPY.statusHeading}</CardTitle>
          <Badge variant={presentation.tone}>{presentation.label}</Badge>
        </div>

        <dl className="mt-3 flex flex-col gap-2 text-sm">
          {confirmations && (
            <div className="flex items-baseline justify-between gap-4">
              <dt className="text-muted">{PAY_COPY.statusConfirmationsLabel}</dt>
              <dd className="font-semibold text-ink">{confirmations}</dd>
            </div>
          )}
          {payment.amountPaid && (
            <div className="flex items-baseline justify-between gap-4">
              <dt className="text-muted">{PAY_COPY.invoiceAmountLabel}</dt>
              <dd className="font-semibold text-ink">{payAmount(payment.amountPaid)}</dd>
            </div>
          )}
        </dl>

        {note && (
          <p className="mt-3 text-sm text-muted" role="status">
            {note}
          </p>
        )}

        {(polling || stale) && (
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <p className="text-xs text-muted" aria-live="polite">
              {stale ? PAY_COPY.statusStaleNote : PAY_COPY.statusPollingNote}
            </p>
            {stale && (
              <Button variant="secondary" size="sm" onClick={onRefresh}>
                {PAY_COPY.statusRefresh}
              </Button>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/** The one line that matches the state, or nothing when the badge says enough. */
function statusNote(status: string): string | null {
  switch (status) {
    case "unpaid":
      return PAY_COPY.statusUnpaidNote;
    case "under_review":
      return PAY_COPY.statusScreeningPending;
    case "paid":
    case "overpaid":
    case "paid_late":
      return PAY_COPY.statusPaidNote;
    default:
      return null;
  }
}
